import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  CogIcon,
  ChevronDownIcon,
  ChevronRightIcon
} from '@heroicons/react/20/solid';
import DisclaimerFooter from '../DisclaimerFooter';

const PortfolioConstructionPage = () => {
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const modelTypes = [
    {
      id: 'long-simple',
      name: 'Long Simple Portfolios',
      description: 'Direct stock selection based on signal strength with transparent, rules-based construction.',
      details: `Long Simple portfolios are our most straightforward approach. They select the top-ranked stocks based on our signals and weight them in a transparent, easy-to-understand manner.

      How it works:
      • Stock Selection: We rank all stocks by their signal strength and select the top N stocks
      • Equal Weighting Base: We start with equal weights and adjust based on market capitalization and liquidity
      • Position Sizing: Each stock typically gets 2-5% of the portfolio, with larger companies getting slightly higher weights
      • Rebalancing: Monthly rebalancing ensures the portfolio stays aligned with current signals

      Best for: Advisors who want transparency and simplicity, with clear explanations for every holding.`
    },
    {
      id: 'long-optimize',
      name: 'Long Optimize Portfolios',
      description: 'Sophisticated mathematical optimization balancing return potential with risk management constraints.',
      details: `Long Optimize portfolios use advanced mathematical techniques to build portfolios that maximize expected returns while controlling for various risks.

      The optimization process:
      • Signal Integration: We combine multiple signals to create an expected return forecast for each stock
      • Risk Model: We estimate how stocks move together and their exposure to different risk factors
      • Constraint Application: We apply limits on sector concentration, individual position sizes, and portfolio turnover
      • Mathematical Solution: We use optimization algorithms to find the best portfolio given all constraints

      Key constraints include:
      • Maximum 3% in any single stock (diversification)
      • Sector weights within 7% of benchmark (sector neutrality)
      • Maximum 20% monthly turnover (transaction cost control)
      • Liquidity requirements based on average daily volume

      Best for: Institutional clients who need sophisticated risk management and can explain the optimization process to their stakeholders.`
    },
    {
      id: 'index-enhance',
      name: 'Index Enhancement',
      description: 'Benchmark-aware portfolios that add value while maintaining index-like characteristics.',
      details: `Index Enhancement portfolios stay close to a benchmark index while making small adjustments to add value. Think of it as "index plus alpha."

      The process:
      • Benchmark Starting Point: We begin with the benchmark index weights (S&P 500, Russell 1000, etc.)
      • Signal-Based Adjustments: We make small weight changes based on our signals
      • Tracking Error Control: We limit how far we deviate from the benchmark
      • Risk Management: We ensure the portfolio maintains similar risk characteristics to the index

      Typical adjustments:
      • Individual stock weights can vary by ±0.5% from benchmark
      • Sector weights stay very close to index weights
      • Overall portfolio correlation with benchmark remains high (>0.95)

      Best for: Conservative clients who want index-like returns with modest outperformance potential and low tracking error.`
    }
  ];

  return (
    <div className="min-h-screen bg-bg-100">
      <div className="px-6 py-8">
        {/* Breadcrumbs */}
        <nav className="mb-6">
          <div className="flex items-center text-sm text-text-300">
            <Link to="/learn" className="hover:text-primary-100 transition-colors">
              Learn
            </Link>
            <ChevronRightIcon className="h-4 w-4 mx-2" />
            <span className="text-text-100">Portfolio Construction</span>
          </div>
        </nav>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-text-100 mb-4 flex items-center gap-3">
            <CogIcon className="h-8 w-8 text-primary-100" />
            Portfolio Construction
          </h1>
          <p className="text-lg text-text-200">
            Our systematic approach to building portfolios from signals to final allocations.
          </p>
        </div>

        {/* Introduction */}
        <div className="mb-8 bg-bg-200 rounded-lg p-6 border border-bg-300">
          <h2 className="text-xl font-semibold text-text-100 mb-4">From Signals to Portfolios</h2>
          <p className="text-text-200 leading-relaxed mb-4">
            Having strong signals is only the first step. Converting those signals into investable portfolios requires careful consideration 
            of risk management, transaction costs, and client objectives. Our portfolio construction process is designed to maximize the 
            value of our signals while managing real-world implementation challenges.
          </p>
          <p className="text-text-200 leading-relaxed">
            We offer three distinct construction methodologies, each designed for different client needs and risk tolerances. 
            From transparent rule-based approaches to sophisticated optimization techniques, our models provide flexible solutions 
            for various investment objectives.
          </p>
        </div>

        {/* Model Portfolio Construction */}
        <div className="space-y-4">
          {modelTypes.map((model) => (
            <div key={model.id} className="bg-bg-200 rounded-lg border border-bg-300">
              <button
                onClick={() => toggleSection(model.id)}
                className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-bg-300 rounded-lg transition-colors"
              >
                <div className="flex items-center gap-3">
                  <CogIcon className="h-5 w-5 text-primary-100" />
                  <div>
                    <h3 className="font-semibold text-text-100">{model.name}</h3>
                    <p className="text-sm text-text-200 mt-1">{model.description}</p>
                  </div>
                </div>
                {expandedSections[model.id] ? (
                  <ChevronDownIcon className="h-5 w-5 text-text-200" />
                ) : (
                  <ChevronRightIcon className="h-5 w-5 text-text-200" />
                )}
              </button>
              {expandedSections[model.id] && (
                <div className="px-6 pb-4">
                  <div className="text-text-200 leading-relaxed whitespace-pre-line">
                    {model.details}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Construction Principles */}
        <div className="mt-8 bg-accent-100/5 rounded-lg p-6 border border-accent-100/20">
          <h3 className="text-lg font-semibold text-text-100 mb-4">Core Construction Principles</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium text-text-100 mb-2">Risk Management</h4>
              <p className="text-sm text-text-200">
                Every portfolio includes explicit risk controls to manage concentration, sector exposure, and overall portfolio volatility.
              </p>
            </div>
            <div>
              <h4 className="font-medium text-text-100 mb-2">Transaction Cost Awareness</h4>
              <p className="text-sm text-text-200">
                We consider liquidity, bid-ask spreads, and market impact when sizing positions and managing turnover.
              </p>
            </div>
            <div>
              <h4 className="font-medium text-text-100 mb-2">Signal Preservation</h4>
              <p className="text-sm text-text-200">
                Portfolio constraints are designed to preserve signal strength while managing implementation challenges.
              </p>
            </div>
            <div>
              <h4 className="font-medium text-text-100 mb-2">Scalability</h4>
              <p className="text-sm text-text-200">
                Our construction methods work across different portfolio sizes and can accommodate various capacity constraints.
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="w-full bg-bg-200">
        <DisclaimerFooter small />
      </div>
    </div>
  );
};

export default PortfolioConstructionPage; 